import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bleu.builders/ui";
import { ScrollArea } from "@components/ui/ScrollArea";
import { RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import React from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

interface ZoomableImageProps {
  src: string;
  alt: string;
}

export const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt }) => {
  return (
    <TransformWrapper
      initialScale={1}
      initialPositionX={0}
      initialPositionY={0}
    >
      {({ zoomIn, zoomOut, resetTransform }) => (
        <div className="relative w-full">
          <div className="absolute top-2 left-2 z-10 flex space-x-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => zoomIn()}
              aria-label="Zoom in"
            >
              <ZoomIn className="size-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => zoomOut()}
              aria-label="Zoom out"
            >
              <ZoomOut className="size-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => resetTransform()}
              aria-label="Reset zoom"
            >
              <RotateCcw className="size-4" />
            </Button>
          </div>
          <TransformComponent>
            <img src={src} alt={alt} className="w-full rounded-lg shadow-md" />
          </TransformComponent>
        </div>
      )}
    </TransformWrapper>
  );
};

interface ReceiptAnalysisProps {
  images: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  processedAnalysisData: any;
}

export function ReceiptAnalysis({
  images,
  processedAnalysisData,
}: ReceiptAnalysisProps) {
  const imageCount = images?.length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Receipt Preview</CardTitle>
          <CardDescription>
            {imageCount === 1
              ? "1 image uploaded"
              : `${imageCount} images uploaded`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="aspect-[3/4] bg-gray-100 rounded-md overflow-hidden">
            <ScrollArea className="h-full">
              <div className="space-y-8 p-2 w-full">
                {images?.map((photo, idx) => (
                  <div key={idx} className="relative">
                    <ZoomableImage
                      src={photo}
                      alt={`Receipt photo ${idx + 1}`}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>OCR Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <pre className="text-xs whitespace-pre-wrap">
              {JSON.stringify(processedAnalysisData, null, 2)}
            </pre>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
